import { useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from '@servicecompressionllc/sc-design-system/icons';
import { queryKeys } from '../../../lib/queryKeys.js';
import { searchSharePeople, type SharePerson } from './shareClient.js';

export const initialsOf = (p: SharePerson): string =>
  (p.name ?? p.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export const displayName = (p: SharePerson): string => p.name ?? p.email;

interface PeoplePickerProps {
  id: string;
  selected: SharePerson[];
  onChange: (people: SharePerson[]) => void;
  /** People who already have it: never offered. */
  excludeIds?: readonly string[];
  placeholder?: string;
}

/** Chips for the people picked, and matches for what's typed. */
export const PeoplePicker = ({
  id,
  selected,
  onChange,
  excludeIds = [],
  placeholder,
}: PeoplePickerProps) => {
  const [text, setText] = useState('');
  const q = text.trim();
  const matches = useQuery({
    queryKey: queryKeys.shareCandidates(q),
    queryFn: () => searchSharePeople(q),
    enabled: q.length > 0,
    placeholderData: (previous) => previous,
  });
  const taken = new Set([...excludeIds, ...selected.map((p) => p.userId)]);
  const options = q ? (matches.data ?? []).filter((p) => !taken.has(p.userId)) : [];

  const add = (person: SharePerson) => {
    onChange([...selected, person]);
    setText('');
  };
  const remove = (userId: string) => onChange(selected.filter((p) => p.userId !== userId));
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const last = selected[selected.length - 1];
    if (event.key === 'Backspace' && text === '' && last) remove(last.userId);
    const first = options[0];
    if (event.key === 'Enter' && first) {
      event.preventDefault();
      add(first);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5 rounded border border-outline-strong bg-surface px-2 py-1.5">
        {selected.map((p) => (
          <span
            key={p.userId}
            className="inline-flex items-center gap-1.5 rounded-full border border-outline-strong bg-surface-hover py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-ink"
          >
            {displayName(p)}
            <button
              type="button"
              aria-label={`Remove ${displayName(p)}`}
              onClick={() => remove(p.userId)}
              className="text-ink-muted hover:text-ink"
            >
              <X size={12} aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={id}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={selected.length === 0 ? placeholder : undefined}
          autoComplete="off"
          className="min-w-20 flex-1 bg-transparent px-1 py-0.5 text-sm text-ink outline-none placeholder:text-ink-subtle"
        />
      </div>
      {options.length > 0 && (
        <ul className="flex flex-col rounded border border-outline bg-surface">
          {options.map((p) => (
            <li key={p.userId}>
              <button
                type="button"
                onClick={() => add(p)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-hover"
              >
                <span
                  aria-hidden
                  className="grid h-7 w-7 flex-none place-items-center rounded-full bg-surface-elevated text-[11px] font-semibold text-ink"
                >
                  {initialsOf(p)}
                </span>
                <span className="flex flex-col text-sm leading-tight">
                  <span className="font-medium text-ink">{displayName(p)}</span>
                  <span className="text-xs text-ink-subtle">{p.email}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {q && matches.isSuccess && options.length === 0 && (
        <p className="px-1 text-xs text-ink-subtle">No one with assistant access matches that.</p>
      )}
    </div>
  );
};
